import { Tabs } from '../redux/tabBar/reducer';
import { ChatMessageData } from '../redux/chatMessages/types';
import { ChatRoomsData } from '../redux/chatRooms/types';
import { ISpecialist, SupportedCurrencies } from '../redux/specialists/types';
import { ClientSpecialistContractStatusEnum, SessionTime, SupportedCurrency } from '@mindly/ui-components';
import { FunnelTypeEnum } from 'src/shared';
import { ReviewData } from '../redux/consultationReviews/consultationsReviews.reducer';
import { PaymentIntent } from '@stripe/stripe-js';

export interface IPromotionalCode {
  id: string;
  /**
   * The percentage value that specifies the size of the discount
   * @example 25
   */
  paymentIntent: PaymentIntent;
  percent_off: number;
  discount_amount: number;
  price_in_currency: string;
  price_default: string;
  code: string;
  name: string;
  end_date: FirebaseDate;
  consultation_id: string | null;
  transaction_currency: SupportedCurrencies;
  onboarding_key?: string;
}

export enum PaidTypes {
  paid_100 = 'paid_100',
  paid_0 = 'paid_0',
}

export enum PossiblePaymentsType {
  'payment' = 'payment',
  'checkout' = 'checkout',
  'subscription' = 'subscription',
  'coupon' = 'coupon',
}

//PsychologistCalendarType****
export type PsycologistCalendarType = {
  id: string;
  psychologist_id: string;
  schedule: Array<{ slot_id: string }>;
};

export type WeeklyAvailabilitiesItem = {
  reason: 'busy' | 'opened' | 'booked';
  isAvailable: boolean;
  time: string;
};

export type PsychologistDevicesProfileType = {
  tokens?: {
    fcmtokens?: string[];
  };
};

// TODO: Psychologist
export type PsychologistProfileType = {
  id: string;
  psychologist_id?: string;
  articles: Array<{
    subtheme: string;
    content: Array<{ photoURL: string; title: string; link: string }>;
  }>;
  education: string;
  experience: string;
  goal: string;
  not_work_with: Array<string>;
  short_description: string;
  specialization: string;
  video: Array<{
    subtheme: string;
    content: Array<{ title: string; url: string }>;
  }>;
  work_with: Array<string>;
};

// TODO: psychologist
export type PsychologistType = {
  id: string;
  consultation_with: string;
  duration: number;
  email: string;
  first_name: string;
  last_name: string;
  price: number;
  focus: string;
  photo: string;
  avatar: string;
  experience: number;
  time_zone: string;
  calendar: PsycologistCalendarType;
  devices?: PsychologistDevicesProfileType;
  profile: PsychologistProfileType;
  psychologistSex?: 'female' | 'male';
  horizontal_avatar: string;
};

/**
 * @deprecated Use Session type instead from \@mindly/ui-components
 */
export type ConsultationType = {
  id: string;
  charge_type: string;
  consultation_time: SessionTime | string;
  consultation_with: string;
  duration: number;
  exchange_rate: string;
  payment_date: {
    _seconds: number;
    nanoseconds: number;
  };
  payment_type?: string;
  price: number;
  price_uah: number;
  psychologist_avatar: string;
  psychologist_id: string;
  psychologist_name: string;
  psychologist_timezone: string;
  user_avatar: string;
  user_id: string;
  user_name: string;
  user_timezone: string;
  videochat_url: string;
  was_rescheduled: boolean;
  session_status?: 'client_showed' | 'client_not_showed';
  consultation_time_before_rescheduling?: {
    _seconds: number;
    nanoseconds: number;
  };
  therapist?: ISpecialist;
  client?: {
    specialist_client_contract_id?: string | null;
    specialist_client_contract_status?: ClientSpecialistContractStatusEnum | null;
  };
  applied_promotional_code?: string | null;
  auto_booking_status?: 'enable' | 'disable';
  subscription_id?: string | null;
  price_type?: string;
  price_default?: number;
  payment_intent_id?: string;
  session_review?: ReviewData;
};

export type ConsultationWatcherType = {
  user_id: string;
  payment_date: {
    seconds: number;
    nanoseconds: number;
  };
};

export type HasPaidConsultationsWithType = {
  therapistID?: string;
  currentPrice?: string;
};
export type SpecialistOrderType = {
  specialist_id: string;
  match: number;
  matching_by_areas?: string[];
  matching_by_specialization?: string[];
};
export type UserType = {
  id: string;
  avatar: string;
  display_name: string;
  last_name?: string;
  displayName?: string;
  email: string;
  phone_number: string;
  phoneNumber?: string;
  surveyAnswers?: QuestionType[];
  user_psychologists?: Array<string> | string;
  time_zone: string;
  consultations?: ConsultationType[];

  /**
   * @deprecated
   */
  client_from_country?: string;
  financial_support_approved?: boolean;
  /**
   * @deprecated
   */
  hasPaidConsultationsWith?: Array<HasPaidConsultationsWithType>;
  favourite_specialist_ids?: Array<string> | null;
  app_lang: string;
  app_reviewed_after_consultation_count?: number;
  onBoardingAnswers?: {
    [key: string]: string[];
  };
  specialist_order?: SpecialistOrderType[] | null;
  onboarding_key?: string | null;
  onboarding_name?: string;
  onboarding_type?: FunnelTypeEnum;
  /**
   * Date of birth in format 'dd.mm.yyyy'
   */
  dob?: string;
  country?: string;
  us_state?: string;
  tax_id?: string;
  is_viewed_specialist_list?: boolean;
  onboarding_experiment_name?: string | null;
  global_personal_price?: number;
  global_personal_price_currency?: SupportedCurrency;
  possible_price_filters?: number[];
};

export type CreateUserEmailFlow = {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  avatar?: File | null;
};

export type CreateUserType = {
  email: string;
  firstName: string;
  lastName: string;
  country?: string;
  us_state?: string;
  dob?: string;
  avatar?: File | null;
  onBoardingKey?: string | null;
  attributionParams?: Record<string, string>;
};

export type QuestionType = {
  answers: string[];
  photoURL: string;
  question: string;
  subquestion: string;
};

export type AnswerType = {
  id: number;
  question: string;
  answer: string;
};

export type SurveyType = {
  questions: QuestionType[];
};

export type InputState = {
  value: string;
  active: boolean;
};

export type ChangeUserDataObject = {
  display_name: string;
  last_name: string;
  phone_number: string;
};

export type TabBarState = {
  isActive: boolean;
  activeTab: Tabs;
};

export type GoogleForgotPasswordAnswer = {
  apiKey: string;
  lang: string;
  mode: string;
  oobCode: string;
};

export interface Customer {
  /**
   * Unique identifier for the object.
   */
  id: string;

  /**
   * String representing the object's type. Objects of the same type share the same value.
   */
  object: 'customer';

  /**
   * Current balance, if any, being stored on the customer. If negative, the customer has credit to apply to their next invoice. If positive, the customer has an amount owed that will be added to their next invoice. The balance does not refer to any unpaid invoices; it solely takes into account amounts that have yet to be successfully applied to any invoice. This balance is only taken into account as invoices are finalized.
   */
  balance: number;

  /**
   * Time at which the object was created. Measured in seconds since the Unix epoch.
   */
  created: number;

  /**
   * Three-letter [ISO code for the currency](https://stripe.com/docs/currencies) the customer can be charged in for recurring billing purposes.
   */
  currency: string | null;

  deleted?: void;

  /**
   * When the customer's latest invoice is billed by charging automatically, `delinquent` is `true` if the invoice's latest charge failed. When the customer's latest invoice is billed by sending an invoice, `delinquent` is `true` if the invoice isn't paid by its due date.
   *
   * If an invoice is marked uncollectible by [dunning](https://stripe.com/docs/billing/automatic-collection), `delinquent` doesn't get reset to `false`.
   */
  delinquent: boolean | null;

  /**
   * An arbitrary string attached to the object. Often useful for displaying to users.
   */
  description: string | null;

  /**
   * The customer's email address.
   */
  email: string | null;

  /**
   * The prefix for the customer used to generate unique invoice numbers.
   */
  invoice_prefix: string | null;

  invoice_settings: {
    /**
     * ID of a payment method that's attached to the customer, to be used as the customer's default payment method for subscriptions and invoices.
     */
    default_payment_method?: string;
  };

  /**
   * Has the value `true` if the object exists in live mode or the value `false` if the object exists in test mode.
   */
  livemode: boolean;

  /**
   * The customer's full name or business name.
   */
  name: string | null;

  /**
   * The suffix of the customer's next invoice number, e.g., 0001.
   */
  next_invoice_sequence?: number;

  /**
   * The customer's phone number.
   */
  phone: string | null;

  /**
   * The customer's preferred locales (languages), ordered by preference.
   */
  preferred_locales: Array<string> | null;
}

export enum MessageTypes {
  MESSAGE,
  UPDATE_AVATAR,
  UPDATE_NAME,
  VIEWED_MESSAGE,
}

export interface BaseMessageType {
  type: MessageTypes;
}

export interface TextMessage extends BaseMessageType, ChatRoomsData {}

export interface UpdateAvatarMessage extends BaseMessageType {
  fromId: string;
  avatar: string;
}

export interface UpdateNameMessage extends BaseMessageType {
  fromId: string;
  name: string;
}

export interface UnReadeMessages extends ChatRoomsData {
  messages: ChatMessageData[];
}

export enum SystemMessagesType {
  'session_booked' = 'session_booked',
  'session_canceled' = 'session_canceled',
  'session_rescheduled' = 'session_rescheduled',

  'subscription_paused' = 'subscription_paused',
  'subscription_un_paused' = 'subscription_un_paused',
  'subscription_create' = 'subscription_create',
  'subscription_cancel' = 'subscription_cancel',
}

export interface IDataForCreateConsultation {
  userTimezone: string;
  userID: string;
  consultationID: string;
  psychologistID: string;
  /**
   * Date in iso format(only utc!)
   */
  consultationTime: string;
  videochatRoomID: string;
  subscriptionID?: string;
}

export interface ViewedMessage extends BaseMessageType {
  message_id: string;
  room_id: string;
}

export enum CurrencySign {
  'EUR' = '€',
  'PLN' = 'zł',
  'UAH' = '₴',
  /*
   * Fix this by priceNormalizer function from ui library
   */
  'USD' = ' USD',
}

export type FirebaseDate = {
  _seconds: number;
  nanoseconds: number;
};

export type SupportedAppVersions = {
  is_app_version_supported: false;
  will_be_deleted: false;
  refresh_interval: number;
};

export interface ILocalizedAreasOfWorkByClientProblem {
  id: string;
  name: string;
  order: number;
  items?: { id: string; label: string; order: number; parentId?: string }[];
}
